/**
 * recordingUpload.js — send a game recording (audio or video) to the server
 * for the official stats pass. 2026-10-17: pulled out of GameStatsPanel so
 * Press Box can upload into any game (and into a game it just created).
 *
 * Parts of ~5 MB (every hop in front of the API caps a request at ~10 MB):
 *   POST /teams/:teamId/recordings/:gameId/upload/start  { mime, size, durationMs, video }
 *   POST .../upload/:uploadId/part?seq=N                  raw bytes
 *   POST .../upload/:uploadId/finish
 * A dropped part is resumed from where the server says it got to, not restarted.
 */
import api from '../services/api.js';

export const RECORDING_ACCEPT = 'audio/*,video/*,.m4a,.mp3,.wav,.webm,.ogg,.aac,.opus,.mp4,.mov,.mkv,.m4v,.3gp';

/**
 * A <video> element reads the length of either kind of file and says whether
 * there is a picture (an iPhone voice memo is "video/mp4" with no picture).
 */
export function probeFile(file) {
  return new Promise((resolve) => {
    try {
      const v = document.createElement('video');
      const url = URL.createObjectURL(file);
      let settled = false;
      const done = (r) => { if (settled) return; settled = true; URL.revokeObjectURL(url); resolve(r); };
      v.preload = 'metadata';
      v.onloadedmetadata = () => done({ durationMs: Number.isFinite(v.duration) ? Math.round(v.duration * 1000) : null, video: v.videoWidth > 0 });
      v.onerror = () => done({ durationMs: null, video: null });
      setTimeout(() => done({ durationMs: null, video: null }), 6000);
      v.src = url;
    } catch { resolve({ durationMs: null, video: null }); }
  });
}

/**
 * 2026-10-21 (per Gilles) — SHA-256 of the file, so the server can say "this
 * exact recording was already uploaded" instead of analysing it twice.
 * Returns null when the browser can't (huge file, old WebView) — the server
 * re-checks on its own copy regardless.
 */
export async function fileSha256(file) {
  try {
    if (!window.crypto?.subtle || file.size > 300 * 1024 * 1024) return null;
    const buf = await file.arrayBuffer();
    const digest = await window.crypto.subtle.digest('SHA-256', buf);
    return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
  } catch { return null; }
}

export function guessMime(file) {
  const ext = String(file?.name || '').split('.').pop()?.toLowerCase();
  return file?.type || ({ m4a: 'audio/mp4', mp4: 'audio/mp4', aac: 'audio/aac', mp3: 'audio/mpeg', wav: 'audio/wav', webm: 'audio/webm', ogg: 'audio/ogg', opus: 'audio/ogg',
    mov: 'video/quicktime', mkv: 'video/x-matroska', m4v: 'video/x-m4v', '3gp': 'video/3gpp' }[ext] || '');
}

/**
 * Upload one file. `onProgress({ pct, phase })` with phase 'sending' then
 * 'processing'. Resolves { video, recordingId } once the server has it (the
 * stats follow minutes later: GAME_STATS_FINAL on the team socket). Throws
 * with the server's message on failure.
 */
export async function uploadRecording({ teamId, gameId, file, sha256 = undefined, appendTo = null, position = 'after', analyze = true, onProgress = () => {} }) {
  const guess = guessMime(file);
  onProgress({ pct: 0, phase: 'sending' });
  const probe = await probeFile(file);
  const video = probe.video === true || (probe.video === null && (/^video\//.test(guess) && guess !== 'video/mp4'));
  // the duplicate check: hash unless the caller already did
  const sha = sha256 !== undefined ? sha256 : await fileSha256(file);
  const { data: st } = await api.post(`/teams/${teamId}/recordings/${gameId}/upload/start`,
    { mime: guess, size: file.size, durationMs: probe.durationMs, video, sha256: sha || undefined,
      // 2026-10-27 — stitch this file onto an existing recording (the
      // recorder died mid-game); analyze=false holds the stats pass while
      // more parts follow.
      appendTo: appendTo || undefined, position: appendTo ? position : undefined, analyze: analyze ? undefined : false });
  const part = Number(st.partBytes) || 5 * 1024 * 1024;
  const base = `/teams/${teamId}/recordings/${gameId}/upload/${st.uploadId}`;
  let seq = 0, off = 0, tries = 0;
  while (off < file.size) {
    const blob = file.slice(off, Math.min(file.size, off + part));
    try {
      await api.post(`${base}/part?seq=${seq}`, blob, { headers: { 'Content-Type': 'application/octet-stream' }, timeout: 120000 });
      off += blob.size; seq++; tries = 0;
    } catch (e) {
      // 2026-10-01 — resume, don't restart: a rink's Wi-Fi drops mid-way
      // through a 3 GB video. Ask the server where it got to and carry
      // on from there; only give up after a run of failures.
      const code = e.response?.status;
      if (code && code !== 409 && code !== 500 && code !== 502 && code !== 504) throw e;
      if (++tries >= 8) throw e;
      await new Promise(r => setTimeout(r, Math.min(15000, 1500 * tries)));
      try { const { data: at } = await api.get(base); seq = at.next; off = at.bytes; } catch { /* keep our own count */ }
    }
    onProgress({ pct: Math.round((off / file.size) * 100), phase: 'sending' });
  }
  onProgress({ pct: 100, phase: 'processing', video });
  const { data: fin } = await api.post(`${base}/finish`, {}, { timeout: 120000 });
  return { video, recordingId: fin?.recordingId || null };
}

/**
 * 2026-10-26 (per Gilles) — upload the ARENA CAMERA's video of the game.
 * Unlike uploadRecording (narration: the picture is dropped), the video is
 * KEPT and goal highlight clips are cut from it. Same resumable part loop.
 */
export const GAME_VIDEO_ACCEPT = 'video/*,.mp4,.mov,.webm,.mkv,.m4v,.3gp';

export async function uploadGameVideo({ teamId, gameId, file, append = false, more = false, onProgress = () => {} }) {
  onProgress({ pct: 0, phase: 'sending' });
  const probe = await probeFile(file);
  const sha = await fileSha256(file);
  // 2026-10-28 — the arena camera splits a game into ~30-min files: append
  // joins this one onto the stored video; more holds the clip build until
  // the last segment.
  const { data: st } = await api.post(`/teams/${teamId}/recordings/${gameId}/video/upload/start`,
    { mime: guessMime(file) || 'video/mp4', size: file.size, durationMs: probe.durationMs, sha256: sha || undefined,
      append: append || undefined, more: more || undefined, fileName: file.name || undefined });
  const part = Number(st.partBytes) || 5 * 1024 * 1024;
  const base = `/teams/${teamId}/recordings/${gameId}/video/upload/${st.uploadId}`;
  let seq = 0, off = 0, tries = 0;
  while (off < file.size) {
    const blob = file.slice(off, Math.min(file.size, off + part));
    try {
      await api.post(`${base}/part?seq=${seq}`, blob, { headers: { 'Content-Type': 'application/octet-stream' }, timeout: 120000 });
      off += blob.size; seq++; tries = 0;
    } catch (e) {
      const code = e.response?.status;
      if (code && code !== 409 && code !== 500 && code !== 502 && code !== 504) throw e;
      if (++tries >= 8) throw e;
      await new Promise(r => setTimeout(r, Math.min(15000, 1500 * tries)));
      try { const { data: at } = await api.get(base); seq = at.next; off = at.bytes; } catch { /* keep our own count */ }
    }
    onProgress({ pct: Math.round((off / file.size) * 100), phase: 'sending' });
  }
  onProgress({ pct: 100, phase: 'processing' });
  await api.post(`${base}/finish`, {}, { timeout: 120000 });
  return { ok: true };
}

/** The server's words for a failed upload, else a plain sentence. */
export const uploadError = (e) => e?.response?.data?.message || e?.response?.data?.error || 'Could not upload the recording';

/**
 * 2026-11-02 (per Gilles: "if I move into another game and come back, the
 * upload has disappeared") — the uploads in flight, kept OUTSIDE any page so
 * a page that comes back shows them again, and a leave-page warning while
 * one is running (the browser does the upload; closing the tab would kill it).
 */
const inFlight = new Map();          // gameId -> { pct, phase, message }
const listeners = new Set();
export const activeUploads = {
  get: () => Object.fromEntries(inFlight),
  set(gameId, state) {
    if (state) inFlight.set(gameId, state); else inFlight.delete(gameId);
    for (const fn of listeners) { try { fn(activeUploads.get()); } catch { /* a stale listener */ } }
  },
  subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
};
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if ([...inFlight.values()].some(u => u.phase === 'sending')) { e.preventDefault(); e.returnValue = 'An upload is still running — leaving now cancels it.'; }
  });
}
